/**
 * FlyingWordAnimation - animates tiles flying from source to destination
 * when a word is claimed or stolen, with letter rearrangement for steals
 */

import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Tile } from './Tile';
import { useGameStore } from '../../stores/gameStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { getPlayerColor } from '../../utils/playerColors';
import { takeClaimSnapshot, type ClaimSnapshot, type SnapshotRect } from '../../lib/claimSnapshots';
import type { StolenFromPlayer } from '../../types/generated';

interface LetterSource {
  letter: string;
  sourceType: 'stolen' | 'middle';
  sourcePlayerId?: string;
  sourceWordId?: string;
  originalIndex?: number;  // Position in original stolen word
}

interface FlyingWord {
  id: string;
  letters: string[];
  letterSources: LetterSource[];
  playerId: string;
  playerColorIndex: number;
  stolenFromPlayers: StolenFromPlayer[];
  snapshot: ClaimSnapshot | null;
  timestamp: number;
}

export function FlyingWordAnimation() {
  const lastEvent = useGameStore((s) => s.lastEvent);
  const reducedMotion = useSettingsStore((s) => s.reducedMotion);
  const [flyingWords, setFlyingWords] = useState<FlyingWord[]>([]);
  const poolRef = useRef<HTMLElement | null>(null);
  const playerAreasRef = useRef<Map<string, HTMLElement>>(new Map());
  const timeoutsRef = useRef<Map<string, number>>(new Map());

  // Find DOM elements - only scan when there are flying words
  useEffect(() => {
    poolRef.current = document.querySelector('[data-tile-pool]');

    const updatePlayerAreas = () => {
      document.querySelectorAll('[data-player-id]').forEach((el) => {
        const playerId = el.getAttribute('data-player-id');
        if (playerId) {
          playerAreasRef.current.set(playerId, el as HTMLElement);
        }
      });
    };

    updatePlayerAreas();

    if (flyingWords.length > 0) {
      const interval = setInterval(updatePlayerAreas, 500);
      return () => clearInterval(interval);
    }
  }, [flyingWords.length]);

  // Listen for word claimed events
  useEffect(() => {
    if (!lastEvent || lastEvent.type !== 'WORD_CLAIMED') return;

    const { claimedWord, playerId, stolenFromPlayers } = lastEvent;

    // Snapshot of stolen words (letters + positions) captured by the socket
    // handler BEFORE the store removed them
    const snapshot = takeClaimSnapshot(claimedWord.id);
    if (reducedMotion) return;

    // Find player color
    const player = useGameStore.getState().gameState?.players.find(p => p.id === playerId);
    const playerColorIndex = player?.colorIndex ?? 0;

    // Analyze letter sources for animation
    const letterSources = analyzeLetterSources(claimedWord.letters, snapshot);

    const newFlyingWord: FlyingWord = {
      id: claimedWord.id,
      letters: claimedWord.letters,
      letterSources,
      playerId,
      playerColorIndex,
      stolenFromPlayers: stolenFromPlayers || [],
      snapshot,
      timestamp: Date.now(),
    };

    setFlyingWords((prev) => {
      if (prev.some((w) => w.id === claimedWord.id)) {
        return prev;
      }
      return [...prev, newFlyingWord];
    });

    // Longer animation for steals (shows rearrangement)
    const duration = stolenFromPlayers && stolenFromPlayers.length > 0 ? 1500 : 800;
    const timeoutId = window.setTimeout(() => {
      timeoutsRef.current.delete(claimedWord.id);
      setFlyingWords((prev) => prev.filter((w) => w.id !== claimedWord.id));
    }, duration);
    timeoutsRef.current.set(claimedWord.id, timeoutId);
  }, [lastEvent, reducedMotion]);

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutsRef.current.clear();
    };
  }, []);

  if (flyingWords.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <AnimatePresence>
        {flyingWords.map((word) => (
          <FlyingWordTiles
            key={word.id}
            word={word}
            poolEl={poolRef.current}
            playerAreasMap={playerAreasRef.current}
          />
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
}

/**
 * Analyze where each letter in the claimed word came from
 */
function analyzeLetterSources(
  claimedLetters: string[],
  snapshot: ClaimSnapshot | null
): LetterSource[] {
  // Collect all letters from stolen words (captured before removal)
  const stolenLetterPool: { letter: string; playerId: string; wordId: string; originalIndex: number }[] = [];

  for (const stolen of snapshot?.stolenWords ?? []) {
    stolen.letters.forEach((letter, idx) => {
      stolenLetterPool.push({ letter, playerId: stolen.playerId, wordId: stolen.wordId, originalIndex: idx });
    });
  }

  // Map each claimed letter to its source
  const usedFromPool: boolean[] = new Array(stolenLetterPool.length).fill(false);

  return claimedLetters.map((letter) => {
    // Try to find this letter in the stolen pool
    const poolIndex = stolenLetterPool.findIndex(
      (poolItem, idx) => !usedFromPool[idx] && poolItem.letter === letter
    );

    if (poolIndex !== -1) {
      usedFromPool[poolIndex] = true;
      return {
        letter,
        sourceType: 'stolen' as const,
        sourcePlayerId: stolenLetterPool[poolIndex].playerId,
        sourceWordId: stolenLetterPool[poolIndex].wordId,
        originalIndex: stolenLetterPool[poolIndex].originalIndex,
      };
    }

    // Letter came from middle
    return {
      letter,
      sourceType: 'middle' as const,
    };
  });
}

interface FlyingWordTilesProps {
  word: FlyingWord;
  poolEl: HTMLElement | null;
  playerAreasMap: Map<string, HTMLElement>;
}

function FlyingWordTiles({ word, poolEl, playerAreasMap }: FlyingWordTilesProps) {
  const isSteal = word.stolenFromPlayers.length > 0;
  const playerColor = getPlayerColor(word.playerColorIndex);

  // Get positions for different sources
  const [positions, setPositions] = useState<{
    poolPos: { x: number; y: number };
    victimPos: { x: number; y: number };
    destPos: { x: number; y: number };
  } | null>(null);

  useEffect(() => {
    const destEl = playerAreasMap.get(word.playerId);
    const victimId = word.stolenFromPlayers[0]?.playerId;
    const victimEl = victimId ? playerAreasMap.get(victimId) : null;

    // Prefer the pool position captured at claim time
    const poolRect: SnapshotRect | undefined = word.snapshot?.poolRect ?? poolEl?.getBoundingClientRect();
    const destRect = destEl?.getBoundingClientRect();
    const victimRect = victimEl?.getBoundingClientRect();

    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    setPositions({
      poolPos: poolRect
        ? { x: poolRect.left + poolRect.width / 2, y: poolRect.top + poolRect.height / 2 }
        : { x: centerX, y: centerY - 100 },
      victimPos: victimRect
        ? { x: victimRect.left + victimRect.width / 2, y: victimRect.top + 40 }
        : { x: centerX - 100, y: centerY },
      destPos: destRect
        ? { x: destRect.left + destRect.width / 2, y: destRect.top + 40 }
        : { x: centerX, y: centerY + 100 },
    });
  }, [word, poolEl, playerAreasMap]);

  if (!positions) return null;

  const letterWidth = 28;
  const totalWidth = word.letters.length * letterWidth;

  // Start position of a stolen letter: its tile in the stolen word as it was
  // on screen before removal (falls back to the victim's card)
  const stolenStart = (source: LetterSource, idx: number) => {
    const snap = word.snapshot?.stolenWords.find((w) => w.wordId === source.sourceWordId);
    if (snap?.rect && snap.letters.length > 0) {
      const tileWidth = snap.rect.width / snap.letters.length;
      return {
        x: snap.rect.left + tileWidth * ((source.originalIndex ?? idx) + 0.5) - letterWidth / 2,
        y: snap.rect.top + snap.rect.height / 2 - letterWidth / 2,
      };
    }
    return {
      x: positions.victimPos.x - totalWidth / 2 + (source.originalIndex ?? idx) * letterWidth,
      y: positions.victimPos.y,
    };
  };

  return (
    <>
      {word.letterSources.map((source, idx) => (
        <motion.div
          key={`${word.id}-letter-${idx}`}
          initial={{
            // Start from source position
            x: source.sourceType === 'stolen'
              ? stolenStart(source, idx).x
              : positions.poolPos.x + (Math.random() - 0.5) * 60,
            y: source.sourceType === 'stolen'
              ? stolenStart(source, idx).y
              : positions.poolPos.y + (Math.random() - 0.5) * 40,
            scale: 1,
            opacity: 1,
            rotate: source.sourceType === 'middle' ? -15 + Math.random() * 30 : 0,
          }}
          animate={{
            // Animate to final position in the word
            x: positions.destPos.x - totalWidth / 2 + idx * letterWidth,
            y: positions.destPos.y,
            scale: 0.85,
            opacity: 0.95,
            rotate: 0,
          }}
          exit={{
            opacity: 0,
            scale: 0.5,
          }}
          transition={{
            type: 'spring',
            stiffness: isSteal ? 120 : 200,
            damping: isSteal ? 18 : 25,
            mass: 0.8,
            // Stagger letters slightly, with middle letters arriving later
            delay: source.sourceType === 'middle' ? 0.2 + idx * 0.03 : idx * 0.02,
          }}
          className="absolute"
          style={{ willChange: 'transform', zIndex: 100 + idx }}
        >
          <motion.div
            animate={{
              boxShadow: isSteal
                ? [`0 0 0px ${playerColor.hex}`, `0 0 20px ${playerColor.hex}`, `0 0 5px ${playerColor.hex}`]
                : `0 0 10px ${playerColor.hex}`,
            }}
            transition={{ duration: 0.5 }}
            className="rounded"
          >
            <Tile letter={source.letter} small />
          </motion.div>
        </motion.div>
      ))}

      {/* Steal indicator flash */}
      {isSteal && (
        <motion.div
          initial={{
            x: positions.victimPos.x - 60,
            y: positions.victimPos.y - 30,
            opacity: 0,
            scale: 0.5,
          }}
          animate={{
            opacity: [0, 1, 1, 0],
            scale: [0.5, 1.2, 1, 0.8],
          }}
          transition={{ duration: 0.6, times: [0, 0.2, 0.5, 1] }}
          className="absolute text-4xl pointer-events-none"
          style={{ zIndex: 150 }}
        >
          ⚡
        </motion.div>
      )}
    </>
  );
}
